import { Box, Button, Card, Table, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useArchiveSeasonalPricingPlanMutation, useListSeasonalPricingPlansQuery } from '../../../services/pricing';
import RequirePricingAdminUser from '../../auth/RequirePricingAdminUser';
import { FilterBar } from '../../filters';
import { TableLoading } from '../../TableLoading';

import { BLUE_BAR, ORANGE_BAR, seasonalPlansPath } from './constants';
import { SeasonalPlanModal } from './SeasonalPlanModal';
import { buildSeasonalPlanColumns } from './seasonalPlanTableColumns';

interface SeasonalPlansListProps {
  programType: string;
}

export function SeasonalPlansList({ programType }: SeasonalPlansListProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const {
    isOpen: isCreateModalOpen,
    handleOpen: handleNewPlan,
    handleClose: handleCreateModalClose,
  } = useOpenClose();

  const { data: plans, isLoading, isError } = useListSeasonalPricingPlansQuery({ programType });
  const [archivePlan] = useArchiveSeasonalPricingPlanMutation();

  const filtered = useMemo(
    () => (plans ?? []).filter((p) => p.name.toLowerCase().includes(search.toLowerCase())),
    [plans, search],
  );

  const handleCreateSuccess = useCallback(
    (planGroupId: string) => {
      navigate(`${seasonalPlansPath(programType)}/${planGroupId}`);
    },
    [navigate, programType],
  );

  const handleSearchTerm = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
  }, []);

  const handleArchive = useCallback(
    async (planGroupId: string) => {
      if (!window.confirm('Are you sure you want to archive this plan?')) return;
      try {
        await archivePlan({ planGroupId }).unwrap();
        toast.success('Plan archived.');
      } catch {
        toast.error('Failed to archive plan.');
      }
    },
    [archivePlan],
  );

  const columns = useMemo(() => buildSeasonalPlanColumns(handleArchive), [handleArchive]);

  if (isError)
    return (
      <p className="pt-16 text-sm text-[var(--color-text-body-secondary)]">
        Unable to load seasonal plans. Please try again.
      </p>
    );

  return (
    <div className="pt-4">
      <SeasonalPlanModal
        isModalOpen={isCreateModalOpen}
        closeModal={handleCreateModalClose}
        programType={programType}
        onSuccess={handleCreateSuccess}
      />

      {/* Card — same Card/Card.Header/Box structure as Pricing/Mappings.tsx + FilteredMappingsList.tsx */}
      <Card padding="sm">
        <Card.Header>
          <Box direction="row" justifyContent="space-between">
            <Box fontSize="md" fontWeight="medium">
              Seasonal Pricing Plans
            </Box>
            <RequirePricingAdminUser>
              <Button as="button" variant="primary" iconPrefix="add" size="md" onClick={handleNewPlan}>
                Seasonal Pricing Plan
              </Button>
            </RequirePricingAdminUser>
          </Box>
        </Card.Header>

        <Box margin="0 lg 0 lg">
          <FilterBar
            filtersState={{ searchTerm: search }}
            handleSearchTerm={handleSearchTerm}
            placeholder="Search by plan name"
          />

          {/* Legend — no Card/Box equivalent for color swatches, so Tailwind stays here */}
          <Box direction="row" alignItems="center" padding="xs" className="gap-24">
            <span className="flex items-center gap-6 text-[12px] text-[var(--color-text-body-secondary)]">
              <span className="rounded-sm shrink-0" style={{ width: 12, height: 12, backgroundColor: ORANGE_BAR }} />
              Above 100%
            </span>
            <span className="flex items-center gap-6 text-[12px] text-[var(--color-text-body-secondary)]">
              <span className="rounded-sm shrink-0" style={{ width: 12, height: 12, backgroundColor: BLUE_BAR }} />
              Below 100%
            </span>
            <span className="flex items-center gap-6 text-[12px] text-[var(--color-text-body-secondary)]">
              <span className="shrink-0" style={{ width: 12, borderTop: '1px dashed #6e727a' }} />
              Unset
            </span>
          </Box>

          {isLoading ? (
            <TableLoading rows={5} />
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--color-text-body-secondary)]">
              {plans?.length ? 'No plans match your search.' : 'No seasonal plans have been created yet.'}
            </p>
          ) : (
            <Table
              rowKey="planGroupId"
              columns={columns}
              rows={filtered}
              useFixedTableLayout
              isScrollable={{ x: true }}
            />
          )}
        </Box>
      </Card>
    </div>
  );
}
